import MiniSearch from "minisearch";
import { getCoreKnowledgeBaseDocs, type KnowledgeBaseDoc } from "@/lib/knowledgeBase";
import { SYNONYM_MAP } from "@/lib/smartSearch";
import { catalog as aiSearchCatalog } from "@/lib/ai-search/catalog";
import { topCandidates } from "@/lib/ai-search/matcher";
import { needsAIAssist, searchWithAI } from "@/lib/ai-search/searchWithAI";
import { fuzzyCorrectQuery } from "@/lib/ai-search/fuzzyCorrect";
import { hrefForTools, type AssistantMessage } from "@/lib/ai-search/assistant";
import { setCurrentSearchLog } from "@/lib/searchTelemetry";
import { trackSiteSearch } from "@/lib/analytics";

/**
 * Local search: a proper fielded/fuzzy full-text index over the
 * knowledge base, built with MiniSearch. Pure synchronous JS for the
 * common case -- no model, no WASM, no network call, nothing here that
 * can meaningfully block the main thread. This is the replacement for
 * both the old hand-rolled synonym matcher and the
 * semantic-embedding Tier 2 attempt: safer than the latter, more
 * capable than the former.
 *
 * One optional network path exists on top of this: when the MiniSearch
 * result itself is weak (see needsAIAssist() in ai-search/searchWithAI.ts),
 * a fdp-search-worker call tries an AI-assisted pick from the same
 * candidate pool. It never replaces or blocks the MiniSearch result --
 * see the AI-assist block inside searchKnowledgeBase() below for
 * exactly how it's merged in, and searchWithAI.ts's own contract for
 * why it can never throw or leave the caller waiting past
 * WORKER_TIMEOUT_MS.
 *
 * Split into two separate indexes, built from two separate lazy
 * modules -- core (tools/FAQ, this module) and guides
 * (blog/personas, knowledgeBaseGuides.ts, imported only when actually
 * needed). The common case -- searching for a tool -- only ever
 * touches core; guides only gets its own lazy import triggered when a
 * query looks like it's actually asking a question (contains "how",
 * "why", "what", "?", "do i", etc.) rather than naming a tool
 * directly, since that's the kind of query a blog article or persona
 * page is actually likely to answer better than a tool's own one-line
 * catalog description.
 */

// Words that carry no real meaning for ranking purposes -- excluded when
// counting how many query terms a result actually covers, so "merge pdf
// and then rotate" doesn't get diluted by "and"/"then" contributing
// nothing to which tool is actually relevant.
const STOP_WORDS = new Set([
  "a", "an", "the", "and", "then", "or", "to", "of", "for", "with", "my",
  "please", "can", "you", "how", "do", "i", "me", "it", "is", "are", "on",
]);

// A query that looks like it's actually asking a question -- these are
// exactly the cases where a blog article or persona page ("how do I
// merge PDFs on my phone", "what's the best free invoicing tool") is
// plausibly a better answer than a tool's own one-line catalog
// description, so these are the only queries that trigger guides'
// separate lazy import at all.
const QUESTION_PATTERN = /\?|^(how|why|what|when|where|which|who|is|are|can|does|do i|should i)\b/i;

function isQuestionLike(query: string): boolean {
  return QUESTION_PATTERN.test(query.trim());
}

let coreIndex: MiniSearch<KnowledgeBaseDoc> | null = null;
let guidesIndex: MiniSearch<KnowledgeBaseDoc> | null = null;
let guidesIndexPromise: Promise<MiniSearch<KnowledgeBaseDoc>> | null = null;

const MINISEARCH_OPTIONS = {
  idField: "id",
  fields: ["title", "description", "keywords"],
  storeFields: ["id", "docType", "title", "description", "href"],
  searchOptions: {
    boost: { title: 3, keywords: 2, description: 1 },
    fuzzy: 0.2,
    prefix: true,
  },
};

function getCoreIndex(): MiniSearch<KnowledgeBaseDoc> {
  if (coreIndex) return coreIndex;
  coreIndex = new MiniSearch<KnowledgeBaseDoc>(MINISEARCH_OPTIONS);
  coreIndex.addAll(getCoreKnowledgeBaseDocs());
  return coreIndex;
}

/** Lazily imports knowledgeBaseGuides.ts (and, with it, the full
 * blog-search.json + personas-data.ts modules) the first time a
 * question-like query actually needs it -- cached after that first
 * build, same as the core index, so this cost is paid at most once per
 * page load, only for someone who actually asked something guides can
 * answer. */
async function getGuidesIndex(): Promise<MiniSearch<KnowledgeBaseDoc>> {
  if (guidesIndex) return guidesIndex;
  if (guidesIndexPromise) return guidesIndexPromise;

  guidesIndexPromise = import("@/lib/knowledgeBaseGuides").then(async ({ getGuideKnowledgeBaseDocs }) => {
    const idx = new MiniSearch<KnowledgeBaseDoc>(MINISEARCH_OPTIONS);
    idx.addAll(await getGuideKnowledgeBaseDocs());
    guidesIndex = idx;
    return idx;
  });
  return guidesIndexPromise;
}

/** Expands a query with known synonyms (reusing the same map that used to
 * power the old Tier 1 matcher) so "shrink pdf" still finds "Compress
 * PDF" even though the words don't look alike -- MiniSearch's own fuzzy
 * matching alone wouldn't catch that. */
function expandQuery(query: string): string {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  const expanded = new Set(words);
  for (const word of words) {
    for (const syn of SYNONYM_MAP[word] ?? []) expanded.add(syn);
  }
  return Array.from(expanded).join(" ");
}

/**
 * MiniSearch's own relevance score under-weights common-but-meaningful
 * terms: in this knowledge base "pdf" appears in a dozen-plus tool
 * titles, so a document matching only a rarer term (like "rotate") can
 * outscore one matching both "rotate" AND "pdf" -- exactly the bug where
 * "merge pdf and then rotate" ranked the unrelated Flip & Rotate image
 * tool above Rotate PDF. This fixes it directly: results are primarily
 * sorted by how many distinct *meaningful* query words they actually
 * matched (stop words excluded), and only fall back to MiniSearch's own
 * score to break ties within the same coverage level.
 *
 * Coverage is checked per original query word against that word's full
 * synonym group, not just the literal word -- otherwise a match found
 * only through synonym expansion (e.g. "compress" matching because the
 * query had "shrink") wouldn't get credited, undercounting exactly the
 * results the synonym expansion was supposed to surface.
 */
// Tools are what someone searching this site is almost always actually
// trying to find -- so when two results are genuinely tied on how many
// query words they cover, the tool should win the tie over a persona
// or blog post. This is deliberately a tiebreak only, not a bonus
// added on top of coverage: an early version of this added a flat
// bonus per doc type, which let a barely-relevant tool (one or two
// words matching by fuzzy/prefix coincidence) jump ahead of a
// genuinely well-matched persona or blog post -- e.g. "shrink a video
// before emailing" stopped surfacing Compress Video at all once a
// vaguely-related blog post's tool-independent bonus made up the gap.
// Real coverage differences must still win; only a true tie falls back
// to doc-type preference.
const DOC_TYPE_PRIORITY: Record<string, number> = {
  tool: 4,
  persona: 2,
  faq: 1,
  blog: 0,
};

/** How many of the query's meaningful words (each with its synonym group) a result's matched terms cover. */
function coverageOf(match: Record<string, string[]>, wordGroups: string[][]): number {
  const matchedTerms = Object.keys(match);
  return wordGroups.filter((group) =>
    matchedTerms.some((term) => group.some((acceptable) => term.startsWith(acceptable) || acceptable.startsWith(term)))
  ).length;
}

/**
 * True when the best result covers too little of what was typed to be called a match: at most half of the
 * meaningful words for short queries ("zzqxv nothing" matched only the filler word), under a third for longer ones.
 * MiniSearch is deliberately forgiving (prefix + fuzzy, OR between words), so without this a nonsense query still
 * returned an unrelated tool list with no "nothing found" message.
 */
export function isWeakMatch(meaningfulWordCount: number, bestCoverage: number): boolean {
  if (meaningfulWordCount === 0) return false;
  return meaningfulWordCount <= 2 ? bestCoverage * 2 <= meaningfulWordCount : bestCoverage * 3 < meaningfulWordCount;
}

function rerankByTermCoverage<T extends { match: Record<string, string[]>; score: number; docType: string }>(
  results: T[],
  queryWords: string[]
): T[] {
  const meaningfulWords = queryWords.filter((w) => !STOP_WORDS.has(w));
  const wordGroups = meaningfulWords.map((w) => [w, ...(SYNONYM_MAP[w] ?? [])]);

  const withCoverage = results.map((r) => ({ result: r, coverage: coverageOf(r.match, wordGroups) }));

  withCoverage.sort((a, b) =>
    b.coverage - a.coverage ||
    (DOC_TYPE_PRIORITY[b.result.docType] ?? 0) - (DOC_TYPE_PRIORITY[a.result.docType] ?? 0) ||
    b.result.score - a.result.score
  );
  return withCoverage.map((x) => x.result);
}

export interface KnowledgeBaseSearchResults {
  /** Set only when the AI assistant had something more useful to add than
   * a tool pick: a clarifying question, or a written answer. Rendered as a
   * card above the normal results (AssistantCard.tsx). */
  assistant?: AssistantMessage | null;
  tools: KnowledgeBaseDoc[];
  faq: KnowledgeBaseDoc[];
  personas: KnowledgeBaseDoc[];
  blog: KnowledgeBaseDoc[];
  /** Whichever single result -- tool, persona, or FAQ entry -- ranked
   * highest overall, by the same relevance used to order everything else.
   * Used for Enter-key "go to the best match" navigation. Every doc type
   * carries a real href, so this is always a valid navigation target.
   * Null when there were no results at all. */
  topResult: KnowledgeBaseDoc | null;
  /** MiniSearch's own raw score for topResult (absent when there were
   * no results at all). Exposed so the optional AI-assist path can decide
   * whether MiniSearch's own top match was actually a strong one --
   * see needsAIAssist() in ai-search/searchWithAI.ts. */
  topScore?: number;
  /** Set when nothing matched what was typed (see isWeakMatch). `tools` then holds only the closest few tools,
   * topResult is null (Enter must not jump to an unrelated tool) and the other groups are empty. */
  noMatch?: boolean;
}

/**
 * Lightweight "as you type" suggestions for the homepage search: tool names only, from the in-memory index.
 * Synchronous, never calls the AI helper, never writes the search log and never fires analytics, so it is safe to
 * run on a typing pause. The real search (Enter / arrow) is searchKnowledgeBase.
 */
export function suggestTools(query: string, limit = 5): KnowledgeBaseDoc[] {
  const q = query.trim();
  if (q.length < 3) return [];
  const queryWords = q.toLowerCase().split(/\s+/).filter(Boolean);
  const meaningful = queryWords.filter((w) => !STOP_WORDS.has(w));
  const raw = getCoreIndex().search(expandQuery(q)).filter((r) => (r as unknown as { docType: string }).docType === "tool");
  const ranked = rerankByTermCoverage(raw as unknown as { match: Record<string, string[]>; score: number; docType: string }[], queryWords);
  const wordGroups = meaningful.map((w) => [w, ...(SYNONYM_MAP[w] ?? [])]);
  const best = ranked[0] ? coverageOf(ranked[0].match, wordGroups) : 0;
  if (!ranked.length || isWeakMatch(meaningful.length, best)) return [];
  return ranked.slice(0, limit) as unknown as KnowledgeBaseDoc[];
}

/**
 * Runs the actual search. Async now (previously synchronous) since a
 * question-like query needs to lazily import and build the separate
 * guides index first -- MiniSearch's own query execution is still fast
 * enough that this resolves essentially immediately once whichever
 * index(es) are needed are ready; the only real asynchronicity is the
 * guides module's own dynamic import when it's actually triggered.
 * Never throws on a normal query -- an empty/nonsense query just
 * returns empty result groups, handled by the UI as a "no matches"
 * state.
 */
export async function searchKnowledgeBase(query: string, limit = 8): Promise<KnowledgeBaseSearchResults> {
  const result = await runSearch(query, limit);
  // One GA4 `search` event per submitted search (and `search_no_results` when nothing matched). Never while typing.
  const empty = !result.assistant && !result.tools.length && !result.faq.length && !result.personas.length && !result.blog.length;
  trackSiteSearch(query, !!result.noMatch || empty);
  return result;
}

async function runSearch(query: string, limit: number): Promise<KnowledgeBaseSearchResults> {
  const q = query.trim();
  if (q.length < 2) return { tools: [], faq: [], personas: [], blog: [], topResult: null, topScore: undefined };

  const queryWords = q.toLowerCase().split(/\s+/).filter(Boolean);
  const expanded = expandQuery(q);

  const coreRaw = getCoreIndex().search(expanded);
  // Only pays for the guides module's own lazy import (and, with it,
  // the full blog-data.ts + personas-data.ts weight) when the query
  // actually looks like a question -- the common "search for a tool"
  // case never touches this at all.
  const guidesRaw = isQuestionLike(q) ? (await getGuidesIndex()).search(expanded) : [];

  const results = rerankByTermCoverage(
    [...coreRaw, ...guidesRaw] as unknown as { match: Record<string, string[]>; score: number; docType: string }[],
    queryWords
  ) as unknown as KnowledgeBaseDoc[];

  const tools = results.filter((r) => r.docType === "tool").slice(0, limit);
  const faq = results.filter((r) => r.docType === "faq").slice(0, 2);
  const personas = results.filter((r) => r.docType === "persona").slice(0, 2);
  const blog = results.filter((r) => r.docType === "blog").slice(0, 2);
  // Every doc type carries a real, correct href, so the single best-ranked
  // result overall is always a valid Enter-key destination. The doc-type
  // tiebreak in rerankByTermCoverage above means tools already win ties
  // over persona/blog/FAQ -- this stays a straight "best overall match"
  // rather than unconditionally forcing a tool to the front regardless
  // of actual relevance, which was tried and made things worse (a
  // barely-matching secondary result beat a correct FAQ answer).
  const topResult = results[0] ?? null;
  const topScore = (results[0] as unknown as { score?: number } | undefined)?.score;

  // ── Optional AI-assist path ─────────────────────────────────────────────────
  // Only runs when MiniSearch's own top match is weak (or there wasn't one).
  // searchWithAI() never throws -- every failure path (network error, timeout,
  // non-200, malformed JSON) resolves to fromAI: false, so no try/catch is
  // needed here, and the existing MiniSearch results above are returned
  // completely unchanged whenever this doesn't produce anything usable.
  // Set only when the AI fallback was actually attempted (had candidates
  // to send) but did not end up supplying the result -- see logSearch.
  let aiMissSource: "ai_failed" | "ai_no_match" | undefined;

  if (needsAIAssist(topScore, q)) {
    // Candidate pool: merge MiniSearch's own top tool matches with
    // matcher.js's topCandidates, rather than using either alone.
    // Confirmed directly, not assumed, why neither alone is enough:
    // for "make my file smaller", matcher.js correctly picks
    // compress-pdf (score 23, a real synonym match: its table knows
    // "smaller" means "compress") while MiniSearch buries it behind
    // "Invitation Card Maker" (matches the literal word "make"). But
    // for "i want to trasfer the file to pdf and then go for singing
    // it" (a real query that surfaced this), matcher.js ties 24
    // different PDF tools at the exact same score -- "pdf" alone
    // matches nearly every PDF tool's keywords -- and picks whichever
    // 5 happen to come first in the catalog array, missing word-to-pdf
    // and sign-pdf entirely. MiniSearch's own fielded/fuzzy index finds
    // word-to-pdf (position 4) that matcher.js's tied scoring missed.
    // Neither system alone covers both cases; the merge does.
    const miniSearchCandidates = results
      .filter((r) => r.docType === "tool")
      .slice(0, 8)
      .map((doc) => ({
        id: doc.id.replace(/^tool-/, ""),
        name: doc.title,
        description: doc.description,
      }));
    const matcherCandidates = topCandidates(q, aiSearchCatalog, 8).map(({ id, name, description }) => ({
      id,
      name,
      description,
    }));
    // A third source: matcher.js run again against a fuzzy-typo-corrected
    // version of the query. Confirmed directly why this is needed on top
    // of the two sources above, not redundant with them: for a query
    // containing "singing" as a typo for "signing", neither MiniSearch's
    // own fuzzy matching nor matcher.js's exact-token synonym lookup
    // found sign-pdf at all (checked MiniSearch's own top-20 results
    // directly -- it wasn't in there). fuzzyCorrectQuery() is
    // deliberately conservative (distance <= 1 under Damerau-Levenshtein,
    // stopwords excluded) specifically so this doesn't introduce noise
    // of its own -- see that file's own history for a real
    // over-correction it found and fixed before this was wired in here.
    // Empty whenever nothing in the query actually needed correcting.
    const correctedQuery = fuzzyCorrectQuery(q);
    const fuzzyCandidates =
      correctedQuery === q
        ? []
        : topCandidates(correctedQuery, aiSearchCatalog, 8).map(({ id, name, description }) => ({
            id,
            name,
            description,
          }));
    // Round-robin interleave (rank 0 from every source, then rank 1,
    // etc.) rather than concatenating each source's full top-N in turn.
    // Confirmed directly why this matters, not just a hypothetical, and
    // twice over: first with the original 8-slot cap (MiniSearch's and
    // matcher.js's top 5 alone, 10 unique candidates with zero overlap,
    // filled the whole pool before a third source was ever reached), and
    // again after raising the cap to 20 -- a later query adding just one
    // more word ("image") shifted all three sources toward
    // image/pdf-conversion tools strongly enough that sign-pdf still
    // didn't make the cut, which is why the Worker's own candidate limit
    // was raised from 8 to 20 in the same change as this. Interleaving
    // means each source's own best pick gets priority over a later-place
    // pick from an earlier source, so a third source's rank-0 result is
    // never silently starved out by the first two sources' later-rank
    // results.
    const seen = new Set<string>();
    const aiCandidates: typeof miniSearchCandidates = [];
    for (let i = 0; i < 8 && aiCandidates.length < 20; i++) {
      for (const source of [miniSearchCandidates, matcherCandidates, fuzzyCandidates]) {
        if (aiCandidates.length >= 20) break;
        const c = source[i];
        if (c && !seen.has(c.id)) {
          seen.add(c.id);
          aiCandidates.push(c);
        }
      }
    }
    // Up to 3 FAQ entries the plain search already found -- the only
    // material the assistant may use to answer a question about the site.
    const faqContext = faq.slice(0, 3).map((d) => ({ q: d.title, a: d.description }));
    const aiResult = await searchWithAI(q, aiCandidates, faqContext);
    if (aiCandidates.length > 0) {
      // ai_failed: it errored, timed out, or was rate-limited.
      // ai_no_match: it answered, but with nothing we could use.
      // Kept separate on purpose: the first is an infrastructure problem
      // (worth knowing about because the free AI tier is rate-limited),
      // the second is a genuine gap in what we offer.
      aiMissSource = aiResult.fromAI ? "ai_no_match" : "ai_failed";
    }

    // The assistant asked a question instead of guessing. topResult is null
    // on purpose: pressing Enter again must not jump to some unrelated
    // "best match" while a question is waiting to be answered.
    if (
      aiResult.fromAI &&
      aiResult.intent === "clarify" &&
      aiResult.question &&
      aiResult.options &&
      aiResult.options.length >= 2
    ) {
      const assistant: AssistantMessage = {
        kind: "clarify",
        text: aiResult.question,
        options: aiResult.options.map((o) => ({ label: o.label, toolIds: o.toolIds, href: hrefForTools(o.toolIds) })),
      };
      logSearch(q, topScore, "ai_clarify", undefined);
      return { tools, faq, personas, blog, topResult: null, topScore, assistant };
    }

    // The visitor asked a question about the tools or the site.
    if (aiResult.fromAI && aiResult.intent === "answer" && aiResult.reply) {
      const related = aiResult.toolIds
        .map((id) => aiCandidates.find((c) => c.id === id))
        .filter((c): c is NonNullable<typeof c> => c !== undefined)
        .map((c) => ({ id: c.id, name: c.name, href: `/tool/${c.id}` }));
      const assistant: AssistantMessage = { kind: "answer", text: aiResult.reply, tools: related };
      logSearch(q, topScore, "ai_answer", undefined);
      return { tools, faq, personas, blog, topResult: null, topScore, assistant };
    }

    if (aiResult.fromAI && aiResult.intent !== "none" && aiResult.toolIds.length > 0) {
      const isWorkflow = aiResult.intent === "workflow" || aiResult.toolIds.length > 1;
      // Looked up from aiCandidates (the pool actually sent), not
      // aiSearchCatalog -- confirmed directly why this matters: a real
      // query got back toolIds: ["universal-file-converter"], a valid
      // suggestion from the MiniSearch-sourced half of the merge above,
      // but that tool isn't in aiSearchCatalog (catalog.js is a separate,
      // incomplete snapshot of the site's tools), so looking it up there
      // silently dropped a correct AI answer and fell through to the
      // unchanged MiniSearch result instead. aiCandidates already carries
      // real name/description for everything the Worker could have validly
      // returned (its own allow-list guarantees toolIds are a subset of
      // what was sent), so this lookup can't miss the way the catalog one did.
      const matchedTools = aiResult.toolIds
        .map((id) => aiCandidates.find((c) => c.id === id))
        .filter((t): t is NonNullable<typeof t> => t !== undefined);

      if (matchedTools.length > 0 && isWorkflow) {
        // Goes through the SAME privileged assistant slot as clarify/
        // answer, not a synthetic row prepended to the plain tools list --
        // confirmed directly as a real problem, not assumed: the panel
        // also runs its own plain keyword-based multi-step guess
        // (parsePipelineRequest) with no awareness of what the AI decided,
        // and that guess was rendering ABOVE this one with none of its
        // nuance (an assumption the AI named, like an unspecified file
        // type, visible here in `reply` but invisible up there). Putting
        // this in `assistant` instead of `tools` lets the panel suppress
        // the redundant, context-blind guess whenever a real one exists,
        // rather than showing both and burying the better one second.
        const assistant: AssistantMessage = {
          kind: "workflow",
          text:
            aiResult.reply ||
            matchedTools.map((t, i) => `${i + 1}. ${t.name}`).join("  "),
          steps: matchedTools.map((t) => ({ id: t.id, name: t.name })),
          href: hrefForTools(aiResult.toolIds),
        };
        const aiToolIdSet = new Set(aiResult.toolIds);
        const dedupedTools = tools.filter((t) => !aiToolIdSet.has(t.id.replace(/^tool-/, "")));
        logSearch(q, topScore, "ai", `ai-workflow-${aiResult.toolIds.join("-")}`);
        return { tools: dedupedTools, faq, personas, blog, topResult: null, topScore, assistant };
      }

      if (matchedTools.length > 0) {
        const aiDoc: KnowledgeBaseDoc = {
          id: matchedTools[0].id,
          docType: "tool",
          title: matchedTools[0].name,
          description: aiResult.reply || matchedTools[0].description,
          keywords: "",
          href: `/tool/${aiResult.toolIds[0]}`,
        };
        // Prepended, not replacing -- but drop any of the original
        // MiniSearch tool results that the AI doc's own toolIds already
        // cover, so the same tool doesn't appear twice in one list (once
        // as part of the AI's pick, once as its own separate MiniSearch
        // row) -- confirmed directly as a real duplicate, not assumed.
        const aiToolIdSet = new Set(aiResult.toolIds);
        const dedupedTools = tools.filter((t) => !aiToolIdSet.has(t.id.replace(/^tool-/, "")));
        dedupedTools.unshift(aiDoc);
        logSearch(q, topScore, "ai", aiDoc.id);
        return { tools: dedupedTools, faq, personas, blog, topResult: aiDoc, topScore };
      }
    }
  }

  const meaningfulWords = queryWords.filter((w) => !STOP_WORDS.has(w));
  const wordGroups = meaningfulWords.map((w) => [w, ...(SYNONYM_MAP[w] ?? [])]);
  const bestCoverage = results.reduce((m, r) => Math.max(m, coverageOf((r as unknown as { match: Record<string, string[]> }).match, wordGroups)), 0);
  if (results.length > 0 && isWeakMatch(meaningfulWords.length, bestCoverage)) {
    // Nothing really matched: say so, offer the closest few tools, and log it as a miss (the existing search log's
    // "none" source feeds the missing-tools review).
    logSearch(q, topScore, "none", undefined);
    return { tools: tools.slice(0, 4), faq: [], personas: [], blog: [], topResult: null, topScore, noMatch: true };
  }

  logSearch(q, topScore, aiMissSource ?? (topResult ? "minisearch" : "none"), topResult?.id);
  return { tools, faq, personas, blog, topResult, topScore };
}

/**
 * Search-query logging. Search only runs when someone submits it
 * (Enter) -- see Layout.tsx's SearchBar -- so every call here is a
 * deliberate search, not a half-typed fragment, and each is logged as
 * it happens. (An earlier version logged on every ~180ms typing pause
 * while search ran as-you-type, which filled the table with prefixes
 * like "htm" / "htmt" / "html to"; that was fixed at the source by
 * making search submit-only rather than by delaying the log.)
 *
 * Fire-and-forget: never awaited by the caller, and any failure is
 * swallowed so logging can never affect the search itself. Uses the
 * same anonymous deviceId as tool-usage logging -- a random,
 * localStorage-persisted UUID never tied to an email or name.
 */
function logSearch(
  query: string,
  topScore: number | undefined,
  resultSource: "minisearch" | "ai" | "none" | "ai_failed" | "ai_no_match" | "ai_clarify" | "ai_answer",
  topResultId: string | undefined,
): void {
  // The stored row's ID is handed to searchTelemetry so that a click on a
  // result can be tied back to this search (recordSearchClick).
  setCurrentSearchLog(
    (async () => {
      try {
        const [{ getOrCreateDeviceId }, { trpcVanilla }] = await Promise.all([
          import("@/lib/deviceId"),
          import("@/lib/trpcVanilla"),
        ]);
        const res = await trpcVanilla.search.log.mutate({
          query,
          topScore,
          resultSource,
          topResultId,
          deviceId: getOrCreateDeviceId(),
        });
        return typeof res?.id === "number" ? res.id : null;
      } catch {
        /* Logging is best-effort only -- never surfaced to the user. */
        return null;
      }
    })(),
  );
}
